import { CalendarDays, Home, Menu, MessageSquare, UsersRound } from 'lucide-react';
import { Link, useLocation } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { ADMIN_ROLES } from '@/utils/roles';

interface MobileAppNavigationProps {
  userRole: string;
  menuOpen: boolean;
  onMenuToggle: () => void;
  showChat: boolean;
}

export const MobileAppNavigation = ({
  userRole,
  menuOpen,
  onMenuToggle,
  showChat,
}: MobileAppNavigationProps) => {
  const { pathname } = useLocation();
  const canViewPeople = (ADMIN_ROLES as readonly string[]).includes(userRole);
  const isClientDetails = pathname.startsWith('/client/');
  const destinations = [
    { to: '/hub', label: 'Hub', icon: Home, active: pathname.startsWith('/hub') },
    {
      to: '/appointments',
      label: 'Schedule',
      icon: CalendarDays,
      active: pathname.startsWith('/appointments'),
    },
    ...(canViewPeople
      ? [
          {
            to: '/users',
            label: 'People',
            icon: UsersRound,
            active: pathname.startsWith('/users') || isClientDetails,
          },
        ]
      : []),
    ...(showChat
      ? [{ to: '/chat', label: 'Chat', icon: MessageSquare, active: pathname.startsWith('/chat') }]
      : []),
  ];
  const moreActive = menuOpen || !destinations.some(({ active }) => active);

  return (
    <>
      <nav
        aria-label="Main navigation"
        className="fixed inset-x-0 bottom-0 z-40 border-t border-[#152319]/10 bg-background/95 pb-[env(safe-area-inset-bottom)] backdrop-blur-xl lg:hidden"
      >
        <div className="mx-auto flex h-16 max-w-xl items-stretch px-2">
          {destinations.map(({ to, label, icon: Icon, active }) => (
            <Button
              key={to}
              asChild
              variant="ghost"
              className="h-full min-w-0 flex-1 flex-col gap-0.5 rounded-none px-1 text-[11px] font-medium hover:bg-transparent active:bg-[#152319]/5"
            >
              <Link
                to={to}
                aria-current={active && !menuOpen ? 'page' : undefined}
                className={cn(active && !menuOpen ? 'text-[#152319]' : 'text-muted-foreground')}
              >
                <span
                  className={cn(
                    'flex h-8 w-12 items-center justify-center rounded-full transition-colors motion-reduce:transition-none',
                    active && !menuOpen && 'bg-[#152319] text-white'
                  )}
                >
                  <Icon className="h-5 w-5" aria-hidden="true" />
                </span>
                {label}
              </Link>
            </Button>
          ))}
          <Button
            variant="ghost"
            onClick={onMenuToggle}
            aria-label={menuOpen ? 'Close menu' : 'Open menu'}
            aria-expanded={menuOpen}
            className={cn(
              'h-full min-w-0 flex-1 flex-col gap-0.5 rounded-none px-1 text-[11px] font-medium hover:bg-transparent active:bg-[#152319]/5',
              moreActive ? 'text-[#152319]' : 'text-muted-foreground'
            )}
          >
            <span
              className={cn(
                'flex h-8 w-12 items-center justify-center rounded-full',
                moreActive && 'bg-[#152319] text-white'
              )}
            >
              <Menu className="h-5 w-5" aria-hidden="true" />
            </span>
            More
          </Button>
        </div>
      </nav>
    </>
  );
};
